import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Dimensions,
	Modal,
	Platform,
	ScrollView,
	StyleSheet,
	Text,
	TextInput,
	TouchableOpacity,
	View,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../constants/fonts';
import { useTheme } from '../../../theme';

export interface KPIFilterDropdownItem {
	id: string | number;
	displayText: string;
	/** When true, item renders in a muted color with an italic " (Inactive)" suffix. Remains selectable. */
	isInactive?: boolean;
	/** Optional secondary label shown in muted text next to the item name (e.g. parent category) */
	subLabel?: string;
	[key: string]: any;
}

interface KPIFilterDropdownProps {
	/** List of options to display */
	childList: KPIFilterDropdownItem[];
	/** Currently selected items (always multi-select) */
	selectedValue: KPIFilterDropdownItem[];
	/** Callback when selection changes */
	setSelectedValue: (value: KPIFilterDropdownItem[]) => void;
	/** Placeholder text when nothing is selected */
	placeholder?: string;
	/** Whether the dropdown is disabled */
	readOnly?: boolean;
	/** Optional style overrides for the container */
	style?: any;
	/** Optional style overrides for the trigger button */
	triggerStyle?: any;
	/** Optional style overrides for the trigger text */
	triggerTextStyle?: any;
	/** Whether this dropdown is currently open (controlled by parent for single-open behavior) */
	isOpen?: boolean;
	/** Request to open/close this dropdown (parent controls single-open) */
	onOpenChange?: (open: boolean) => void;
	/** When true, selecting one item deselects all others (radio-style) and closes dropdown */
	singleSelect?: boolean;
	/** Test ID */
	testID?: string;
	/** Auto-focus search input when dropdown opens (opt-in) */
	autoFocusSearchOnOpen?: boolean;
	/** When true, items are sorted alphabetically when no search query is active */
	sortItems?: boolean;
	/** Set of item IDs that should be shown but not selectable (grayed out) */
	disabledIds?: Set<string>;
	/** Force blue active border on trigger even when dropdown is closed */
	forceActiveTrigger?: boolean;
	/** When true, item toggles stay local until Apply is clicked */
	deferSelectionUntilApply?: boolean;
	/** Show Reset and Apply actions when there are pending local changes */
	showApplyResetActions?: boolean;
}

const KPIFilterDropdown: React.FC<KPIFilterDropdownProps> = ({
	childList = [],
	selectedValue = [],
	setSelectedValue,
	placeholder = 'All',
	readOnly = false,
	style = {},
	triggerStyle,
	triggerTextStyle,
	isOpen: controlledIsOpen,
	onOpenChange,
	singleSelect = false,
	testID,
	autoFocusSearchOnOpen = false,
	sortItems = false,
	disabledIds,
	forceActiveTrigger = false,
	deferSelectionUntilApply = false,
	showApplyResetActions = false,
}) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
	const isControlled = controlledIsOpen !== undefined && onOpenChange !== undefined;
	const [internalIsOpen, setInternalIsOpen] = useState(false);
	const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

	const [searchText, setSearchText] = useState('');
	const dropdownRef = useRef<any>(null);
	const [dropdownPosition, setDropdownPosition] = useState<{
		top?: number;
		bottom?: number;
		left: number;
		width: number;
		maxHeight?: number;
	}>({
		left: 0,
		width: 0,
	});
	const scrollListenerRef = useRef<(() => void) | null>(null);

	const [selectedItems, setSelectedItems] = useState<KPIFilterDropdownItem[]>(selectedValue);

	const normalizeId = (item: KPIFilterDropdownItem) => String(item.id).trim().toLowerCase();

	const areSelectionsEqual = (
		left: KPIFilterDropdownItem[],
		right: KPIFilterDropdownItem[]
	): boolean => {
		if (left.length !== right.length) return false;
		const rightSet = new Set(right.map(normalizeId));
		return left.every((item) => rightSet.has(normalizeId(item)));
	};

	// Sync from parent
	useEffect(() => {
		setSelectedItems(selectedValue);
	}, [selectedValue]);

	// Compute filtered items directly (no useMemo) to avoid stale closure issues with Modal on web
	const query = searchText.trim().toLowerCase();
	let filteredItems: KPIFilterDropdownItem[];
	if (!query) {
		filteredItems = [...childList];
		if (sortItems) {
			filteredItems.sort((a, b) => a.displayText.localeCompare(b.displayText));
		}
	} else {
		// Filter to only matching items
		filteredItems = childList.filter((item) =>
			item.displayText.toLowerCase().includes(query)
		);

		// Sort by relevance: exact match → starts-with → contains → alphabetical within group
		filteredItems.sort((a, b) => {
			const aText = a.displayText.toLowerCase();
			const bText = b.displayText.toLowerCase();

			const aExact = aText === query;
			const bExact = bText === query;
			if (aExact && !bExact) return -1;
			if (!aExact && bExact) return 1;

			const aStarts = aText.startsWith(query);
			const bStarts = bText.startsWith(query);
			if (aStarts && !bStarts) return -1;
			if (!aStarts && bStarts) return 1;

			// Same relevance group — sort alphabetically
			return aText.localeCompare(bText);
		});
	}

	const setOpen = (open: boolean) => {
		if (isControlled) {
			onOpenChange!(open);
		} else {
			setInternalIsOpen(open);
		}
	};

	// Recalculate dropdown position from trigger's current bounding rect (web: fixed positioning).
	// Always opens below the trigger. The menu has a fixed maxHeight and is scrollable internally.
	const recalculatePosition = useCallback(() => {
		if (Platform.OS !== 'web') return;
		const node = dropdownRef.current;
		if (!node || typeof (node as any).getBoundingClientRect !== 'function') return;
		try {
			const rect = (node as any).getBoundingClientRect();
			setDropdownPosition({
				top: rect.bottom + 4,
				left: rect.left,
				width: rect.width,
			});
		} catch {
			// ignore
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Attach/detach scroll listeners when dropdown opens/closes (web only)
	useEffect(() => {
		if (Platform.OS !== 'web') return;

		if (isOpen) {
			// Listen on window + all scrollable ancestors (capture phase catches nested scrolls)
			if (typeof window !== 'undefined') {
				window.addEventListener('scroll', recalculatePosition, true);
				window.addEventListener('resize', recalculatePosition);
				scrollListenerRef.current = () => {
					window.removeEventListener('scroll', recalculatePosition, true);
					window.removeEventListener('resize', recalculatePosition);
				};
			}
		} else {
			// Cleanup
			if (scrollListenerRef.current) {
				scrollListenerRef.current();
				scrollListenerRef.current = null;
			}
		}

		return () => {
			if (scrollListenerRef.current) {
				scrollListenerRef.current();
				scrollListenerRef.current = null;
			}
		};
	}, [isOpen, recalculatePosition]);

	const toggleDropdown = () => {
		if (readOnly) return;

		if (!isOpen) {
			const APPROX_MENU_HEIGHT = 320;
			const screenH = Dimensions.get('window').height;

			if (Platform.OS !== 'web') {
				if (dropdownRef.current && typeof dropdownRef.current.measureInWindow === 'function') {
					dropdownRef.current.measureInWindow(
						(x: number, y: number, w: number, h: number) => {
							const spaceBelow = screenH - (y + h);
							const flipped = spaceBelow < APPROX_MENU_HEIGHT + 8;
							setDropdownPosition({
								top: flipped ? y - APPROX_MENU_HEIGHT : y + h + 4,
								left: x,
								width: w,
							});
							setOpen(true);
						}
					);
				} else {
					setOpen(true);
				}
			} else {
				try {
					if (dropdownRef.current && typeof dropdownRef.current.getBoundingClientRect === 'function') {
						const rect = dropdownRef.current.getBoundingClientRect();
						// Always open below the trigger; menu is internally scrollable
						setDropdownPosition({
							top: rect.bottom + 4,
							left: rect.left,
							width: rect.width,
						});
					}
					setOpen(true);
				} catch {
					setOpen(true);
				}
			}
		} else {
			setOpen(false);
		}
	};

	const isItemDisabled = (item: KPIFilterDropdownItem): boolean => {
		if (!disabledIds || disabledIds.size === 0) return false;
		return disabledIds.has(String(item.id));
	};

	const hasPendingChanges = useMemo(() => {
		if (!deferSelectionUntilApply || !showApplyResetActions) return false;
		return !areSelectionsEqual(selectedItems, selectedValue);
	}, [deferSelectionUntilApply, selectedItems, selectedValue, showApplyResetActions]);

	const handleItemToggle = (item: KPIFilterDropdownItem) => {
		// Prevent selection of disabled items
		if (isItemDisabled(item)) return;

		if (singleSelect) {
			// Single-select mode: selecting one item deselects all others, then close dropdown
			const newSelection = [item];
			setSelectedItems(newSelection);
			if (!deferSelectionUntilApply) {
				setSelectedValue(newSelection);
			}
			// Close dropdown after selection in single-select mode
			handleClose();
			return;
		}

		const isSelected = selectedItems.some(
			(s) => String(s.id).toLowerCase() === String(item.id).toLowerCase()
		);

		let newSelection: KPIFilterDropdownItem[];
		if (isSelected) {
			newSelection = selectedItems.filter(
				(s) => String(s.id).toLowerCase() !== String(item.id).toLowerCase()
			);
		} else {
			newSelection = [...selectedItems, item];
		}

		setSelectedItems(newSelection);
		if (!deferSelectionUntilApply) {
			setSelectedValue(newSelection);
		}
		// Dropdown stays open - no close on item selection
	};

	const handleSelectAll = () => {
		// When search is active, only select the currently filtered/visible items
		// Merge with any existing selections that are not in the filtered set
		// Always exclude disabled items from "Select All"
		if (searchText.trim()) {
			const filteredIds = new Set(filteredItems.map(item => String(item.id).toLowerCase()));
			const existingNonFiltered = selectedItems.filter(
				s => !filteredIds.has(String(s.id).toLowerCase())
			);
			const selectableFiltered = filteredItems.filter(item => !isItemDisabled(item));
			const newSelection = [...existingNonFiltered, ...selectableFiltered];
			setSelectedItems(newSelection);
			if (!deferSelectionUntilApply) {
				setSelectedValue(newSelection);
			}
		} else {
			const selectableItems = childList.filter(item => !isItemDisabled(item));
			setSelectedItems([...selectableItems]);
			if (!deferSelectionUntilApply) {
				setSelectedValue([...selectableItems]);
			}
		}
	};

	const handleClear = () => {
		setSelectedItems([]);
		if (!deferSelectionUntilApply) {
			setSelectedValue([]);
		}
	};

	const handleResetSelection = () => {
		setSelectedItems(selectedValue);
	};

	const handleApplySelection = () => {
		setSelectedValue(selectedItems);
		handleClose();
	};

	const handleClose = () => {
		if (deferSelectionUntilApply) {
			setSelectedItems(selectedValue);
		}
		setOpen(false);
		setSearchText('');
	};

	const getDisplayText = (): string => {
		if (selectedItems.length === 0) {
			return placeholder;
		}

		// In single-select mode, show the selected item's display text directly
		if (singleSelect && selectedItems.length === 1) {
			const item = selectedItems[0];
			if (String(item.id).trim().toLowerCase() === 'all') {
				return 'All';
			}
			return item.displayText;
		}

		// Count non-"All" selected items
		const nonAllSelected = selectedItems.filter(
			(s) => String(s.id).trim().toLowerCase() !== 'all'
		);
		const nonAllOptions = childList.filter(
			(s) => String(s.id).trim().toLowerCase() !== 'all'
		);

		// If only the "All" item is selected (single selection with id "all"), show "All"
		if (selectedItems.length === 1 && String(selectedItems[0].id).trim().toLowerCase() === 'all') {
			return 'All';
		}
		// If all non-"All" options are selected, show "All"
		if (nonAllOptions.length > 0 && nonAllSelected.length >= nonAllOptions.length) {
			return 'All';
		}
		// If all items in the list are selected (including "All"), show "All"
		if (selectedItems.length === childList.length && childList.length > 0) {
			return 'All';
		}

		// Show count of non-"All" selections
		const displayCount = nonAllSelected.length > 0 ? nonAllSelected.length : selectedItems.length;

		// If exactly one item is selected, show its label instead of "1 selected"
		if (displayCount === 1) {
			const singleItem = nonAllSelected.length > 0 ? nonAllSelected[0] : selectedItems[0];
			return singleItem.displayText;
		}

		return `${displayCount} selected`;
	};

	const isItemSelected = (item: KPIFilterDropdownItem) =>
		selectedItems.some((s) => String(s.id).toLowerCase() === String(item.id).toLowerCase());

	const renderItem = ({ item }: { item: KPIFilterDropdownItem }) => {
		const selected = isItemSelected(item);
		return (
			<TouchableOpacity
				style={[dropdownStyles.item, selected && dropdownStyles.itemSelected]}
				onPress={() => handleItemToggle(item)}
				activeOpacity={0.7}
			>
				<View style={[dropdownStyles.checkbox, selected && dropdownStyles.checkboxChecked]}>
					{selected && <MaterialIcons name="check" size={14} color="#FFFFFF" />}
				</View>
				<View style={dropdownStyles.itemLabelWrap}>
					<Text style={dropdownStyles.itemText} numberOfLines={1}>
						{item.displayText}
					</Text>
					{item.subLabel ? (
						<Text style={dropdownStyles.itemSubLabel} numberOfLines={1}>
							{item.subLabel}
						</Text>
					) : null}
				</View>
			</TouchableOpacity>
		);
	};

	return (
		<View style={[dropdownStyles.container, style]}>
			<TouchableOpacity
				ref={dropdownRef}
				style={[
					dropdownStyles.trigger,
					isDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default },
					(isOpen || forceActiveTrigger) && dropdownStyles.triggerActive,
					triggerStyle,
					triggerStyle,
					(isOpen || forceActiveTrigger) && dropdownStyles.triggerActive,
					readOnly && dropdownStyles.triggerDisabled,
				]}
				onPress={toggleDropdown}
				disabled={readOnly}
				testID={testID}
				activeOpacity={0.7}
			>
				<Text
					style={[
						dropdownStyles.triggerText,
						isDark && { color: colors.text.primary },
						triggerTextStyle,
						selectedItems.length === 0 && dropdownStyles.triggerPlaceholder,
						isDark && selectedItems.length === 0 && { color: colors.text.disabled },
					]}
					numberOfLines={1}
				>
					{getDisplayText()}
				</Text>
				<MaterialIcons
					name={isOpen ? 'expand-less' : 'expand-more'}
					size={20}
					color={readOnly ? '#ADB5BD' : (isDark ? colors.text.secondary : '#495057')}
				/>
			</TouchableOpacity>

			<Modal
				visible={isOpen}
				transparent
				animationType="fade"
				supportedOrientations={['portrait', 'landscape']}
				onRequestClose={handleClose}
			>
				<TouchableOpacity
					style={dropdownStyles.overlay}
					activeOpacity={1}
					onPress={handleClose}
				>
				<TouchableOpacity
						activeOpacity={1}
					style={[
						dropdownStyles.menu,
						isDark && { backgroundColor: colors.background.elevated },
						{
							// top is set when opening downward; bottom is set when flipped upward.
							// Only one of these will be defined at a time.
							top: dropdownPosition.top,
							bottom: dropdownPosition.bottom,
							left: dropdownPosition.left,
							// Use trigger width but enforce a readable minimum
							width: Math.max(dropdownPosition.width || 0, 260),
							// Override the static maxHeight with the dynamically computed one
							// so the menu never exceeds the available viewport space.
							...(dropdownPosition.maxHeight != null
								? { maxHeight: dropdownPosition.maxHeight }
								: {}),
						},
					]}
						onPress={(e) => e.stopPropagation()}
					>
						{/* Sticky header: Search + Actions */}
						<View style={[dropdownStyles.stickyHeader, isDark && { backgroundColor: colors.background.elevated, borderBottomColor: colors.border.divider }]}>
							{/* Search bar */}
							<View style={[dropdownStyles.searchRow, isDark && { borderBottomColor: colors.border.divider }]}>
								<MaterialIcons name="search" size={18} color={isDark ? colors.text.secondary : '#7C8086'} />
								<TextInput
									style={[
										dropdownStyles.searchInput,
										isDark && { color: colors.text.primary },
										Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : null,
									]}
									placeholder="Search..."
									placeholderTextColor={isDark ? colors.text.disabled : '#9AA1AB'}
									value={searchText}
									onChangeText={setSearchText}
									autoFocus={autoFocusSearchOnOpen}
								/>
								{searchText ? (
									<TouchableOpacity onPress={() => setSearchText('')}>
										<MaterialIcons name="close" size={16} color="#6D737C" />
									</TouchableOpacity>
								) : null}
							</View>

							{/* Select All / Clear actions */}
							<View style={[dropdownStyles.actionsRow, singleSelect && dropdownStyles.actionsRowCentered]}>
								{!singleSelect && (
									<>
										<TouchableOpacity onPress={handleSelectAll} style={dropdownStyles.actionButton}>
											<Text style={dropdownStyles.actionText}>Select All</Text>
										</TouchableOpacity>
										<View style={dropdownStyles.actionDivider} />
									</>
								)}
								<TouchableOpacity onPress={handleClear} style={dropdownStyles.actionButton}>
									<Text style={dropdownStyles.actionText}>Clear</Text>
								</TouchableOpacity>
							</View>

							{showApplyResetActions && hasPendingChanges ? (
								<View style={dropdownStyles.applyResetRow}>
									<TouchableOpacity
										onPress={handleResetSelection}
										style={[dropdownStyles.applyResetButton, dropdownStyles.resetButton]}
									>
										<Text style={dropdownStyles.resetButtonText}>Reset</Text>
									</TouchableOpacity>
									<TouchableOpacity
										onPress={handleApplySelection}
										style={[dropdownStyles.applyResetButton, dropdownStyles.applyButton]}
									>
										<Text style={dropdownStyles.applyButtonText}>Apply</Text>
									</TouchableOpacity>
								</View>
							) : null}
						</View>

						{/* Options list - Web uses View with overflowY to avoid ScrollView re-render bug; native uses ScrollView for touch scrolling */}
						{Platform.OS === 'web' ? (
							<View style={dropdownStyles.list} key={`list-${searchText.trim().toLowerCase()}`}>
								{filteredItems.length === 0 ? (
									<Text style={[dropdownStyles.emptyText, isDark && { color: colors.text.secondary }]}>No items found</Text>
								) : (
									filteredItems.map((item, index) => {
										const selected = isItemSelected(item);
										const disabled = isItemDisabled(item);
										return (
											<TouchableOpacity
												key={`${String(item.id)}-${item.displayText}-${index}`}
												style={[dropdownStyles.item, selected && !disabled && dropdownStyles.itemSelected, disabled && dropdownStyles.itemDisabled]}
												onPress={() => handleItemToggle(item)}
												activeOpacity={disabled ? 1 : 0.7}
											>
												<View style={[dropdownStyles.checkbox, selected && !disabled && dropdownStyles.checkboxChecked, disabled && dropdownStyles.checkboxDisabled]}>
													{selected && !disabled && <MaterialIcons name="check" size={14} color="#FFFFFF" />}
												</View>
												<View style={dropdownStyles.itemLabelWrap}>
													<Text style={[dropdownStyles.itemText, disabled && dropdownStyles.itemTextDisabled]} numberOfLines={1}>
														{item.displayText}
													</Text>
													{item.subLabel ? (
														<Text style={dropdownStyles.itemSubLabel} numberOfLines={1}>
															{item.subLabel}
														</Text>
													) : null}
												</View>
												{disabled && <MaterialIcons name="lock" size={12} color="#ADB5BD" />}
											</TouchableOpacity>
										);
									})
								)}
							</View>
						) : (
							<ScrollView
								style={dropdownStyles.listNative}
								nestedScrollEnabled
								keyboardShouldPersistTaps="handled"
								showsVerticalScrollIndicator
							>
								{filteredItems.length === 0 ? (
									<Text style={[dropdownStyles.emptyText, isDark && { color: colors.text.secondary }]}>No items found</Text>
								) : (
									filteredItems.map((item, index) => {
										const selected = isItemSelected(item);
										const disabled = isItemDisabled(item);
										return (
											<TouchableOpacity
												key={`${String(item.id)}-${item.displayText}-${index}`}
												style={[
													dropdownStyles.item,
													isDark && { backgroundColor: 'transparent' },
													selected && !disabled && dropdownStyles.itemSelected,
													selected && !disabled && isDark && { backgroundColor: colors.background.surface },
													disabled && dropdownStyles.itemDisabled,
												]}
												onPress={() => handleItemToggle(item)}
												activeOpacity={disabled ? 1 : 0.7}
											>
												<View style={[dropdownStyles.checkbox, isDark && { borderColor: colors.border.default, backgroundColor: colors.background.surface }, selected && !disabled && dropdownStyles.checkboxChecked, disabled && dropdownStyles.checkboxDisabled]}>
													{selected && !disabled && <MaterialIcons name="check" size={14} color="#FFFFFF" />}
												</View>
												<View style={dropdownStyles.itemLabelWrap}>
													<Text style={[dropdownStyles.itemText, isDark && { color: colors.text.primary }, disabled && dropdownStyles.itemTextDisabled]} numberOfLines={1}>
														{item.displayText}
													</Text>
													{item.subLabel ? (
														<Text style={dropdownStyles.itemSubLabel} numberOfLines={1}>
															{item.subLabel}
														</Text>
													) : null}
												</View>
												{disabled && <MaterialIcons name="lock" size={12} color="#ADB5BD" />}
											</TouchableOpacity>
										);
									})
								)}
							</ScrollView>
						)}

						{/* Selection count footer */}
						<View style={[dropdownStyles.footer, isDark && { backgroundColor: colors.background.surface, borderTopColor: colors.border.divider }]}>
							<Text style={[dropdownStyles.footerText, isDark && { color: colors.text.secondary }]}>
								{selectedItems.length} of {childList.length} selected
							</Text>
						</View>
					</TouchableOpacity>
				</TouchableOpacity>
			</Modal>
		</View>
	);
};

const dropdownStyles = StyleSheet.create({
	container: {
		width: '100%',
	},
	trigger: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingHorizontal: 12,
		paddingVertical: 10,
		borderWidth: 1,
		borderColor: '#DEE2E6',
		borderRadius: 8,
		backgroundColor: '#FFFFFF',
		minHeight: 40,
	},
	triggerActive: {
		borderColor: '#0B5FC6',
	},
	triggerDisabled: {
		opacity: 0.5,
		backgroundColor: '#F8F9FA',
	},
	triggerText: {
		flex: 1,
		fontSize: 14,
		fontFamily: FontFamily.medium,
		color: '#2D2F33',
		marginRight: 8,
	},
	triggerPlaceholder: {
		color: '#7C8086',
	},
	overlay: {
		flex: 1,
		backgroundColor: 'rgba(0,0,0,0.15)',
	},
	menu: {
		position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
		backgroundColor: '#FFFFFF',
		borderRadius: 10,
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.15,
		shadowRadius: 12,
		elevation: 8,
		maxHeight: Dimensions.get('window').height * 0.45,
		overflow: 'hidden',
		zIndex: 9999,
	},
	stickyHeader: {
		backgroundColor: '#FFFFFF',
		borderBottomWidth: 1,
		borderBottomColor: '#E9ECEF',
	},
	searchRow: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingHorizontal: 12,
		paddingVertical: 8,
		gap: 8,
		borderBottomWidth: 1,
		borderBottomColor: '#F1F3F5',
	},
	searchInput: {
		flex: 1,
		fontSize: 14,
		fontFamily: FontFamily.medium,
		color: '#2D2F33',
		paddingVertical: 4,
		width: '100%',
	},
	actionsRow: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingHorizontal: 12,
		paddingVertical: 8,
	},
	actionButton: {
		paddingVertical: 4,
		paddingHorizontal: 8,
	},
	actionText: {
		fontSize: 13,
		fontFamily: FontFamily.bold,
		color: '#0B5FC6',
	},
	actionDivider: {
		width: 1,
		height: 16,
		backgroundColor: '#DEE2E6',
		marginHorizontal: 4,
	},
	applyResetRow: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'flex-end',
		gap: 8,
		paddingHorizontal: 12,
		paddingBottom: 8,
	},
	applyResetButton: {
		paddingHorizontal: 12,
		paddingVertical: 6,
		borderRadius: 6,
		borderWidth: 1,
	},
	resetButton: {
		backgroundColor: '#FFFFFF',
		borderColor: '#C7D2E5',
	},
	applyButton: {
		backgroundColor: '#0B5FC6',
		borderColor: '#0B5FC6',
	},
	resetButtonText: {
		fontSize: 13,
		fontFamily: FontFamily.bold,
		color: '#1F2937',
	},
	applyButtonText: {
		fontSize: 13,
		fontFamily: FontFamily.bold,
		color: '#FFFFFF',
	},
	list: {
		maxHeight: Dimensions.get('window').height * 0.28,
		...(Platform.OS === 'web' ? { overflowY: 'auto' } : {}),
		width: '100%',
	} as any,
	listNative: {
		maxHeight: Dimensions.get('window').height * 0.28
	},
	item: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingVertical: 10,
		paddingHorizontal: 12,
		gap: 10,
	},
	itemSelected: {
		backgroundColor: '#F0F6FF',
	},
	itemDisabled: {
		opacity: 0.5,
		backgroundColor: '#F8F9FA',
	},
	checkbox: {
		width: 20,
		height: 20,
		borderRadius: 4,
		borderWidth: 1.5,
		borderColor: '#ADB5BD',
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: '#FFFFFF',
	},
	checkboxChecked: {
		backgroundColor: '#0B5FC6',
		borderColor: '#0B5FC6',
	},
	checkboxDisabled: {
		borderColor: '#DEE2E6',
		backgroundColor: '#F1F3F5',
	},
	itemLabelWrap: {
		flex: 1,
	},
	itemText: {
		fontSize: 14,
		fontFamily: FontFamily.medium,
		color: '#343A40',
	},
	itemSubLabel: {
		fontSize: 12,
		fontFamily: FontFamily.medium,
		color: '#9AA1AB',
		marginTop: 2,
	},
	itemTextDisabled: {
		color: '#ADB5BD',
	},
	itemTextInactive: {
		color: '#ADB5BD',
	},
	itemTextInactiveSuffix: {
		fontStyle: 'italic',
		color: '#ADB5BD',
	},
	emptyText: {
		padding: 16,
		textAlign: 'center',
		fontSize: 13,
		fontFamily: FontFamily.medium,
		color: '#6C757D',
	},
	footer: {
		borderTopWidth: 1,
		borderTopColor: '#E9ECEF',
		paddingVertical: 8,
		paddingHorizontal: 12,
		backgroundColor: '#F8F9FA',
	},
	footerText: {
		fontSize: 12,
		fontFamily: FontFamily.medium,
		color: '#6B6E72',
		textAlign: 'center',
	},
});

export default KPIFilterDropdown;

